import { forwardRef, memo, useCallback, useEffect, useMemo, useState, useContext } from 'react';
import isNil from 'lodash/isNil';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// Skillmore UI Components
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import CardFooter from '@empathco/ui-components/src/elements/CardFooter';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import CardsGrid from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import { JobLookupItem } from '../graphql/customTypes';
import { Skill, SkillLevel, SKILL_LEVEL_FIRST } from '../models/skill';
import { getTbErrorMessage } from '../constants/teamBuilderErrors';
import { PATH_HR_TEAM_BUILDER_TEAM } from '../config/paths';
import { SupervisorContext, TbAddTeamFeedback } from '../context/supervisor';
import LargeButton from '../elements/LargeButton';
import TbTeamCard, { TbTeamCardItem, TbTeamCardProps } from '../v3/TbTeamCard';
import PaginationControls from '../v3/PaginationControls';
import AddJobDialog from '../widgets/AddJobDialog';
// SCSS imports
import { footer } from './TeamBuilder.module.scss';

const componentProps: Partial<TbTeamCardProps> = {
  hrbp: true
};

const TeamBuilder = forwardRef<HTMLDivElement>((_props, ref) => {
  const navigate = useNavigate();
  const {
    tbTeams: { data: teams, count, pending, failed }, requireTbTeams,
    tbTeamAdd: { pending: addPending, failed: addFailed, params: addParams }, tbAddTeam,
    tbTeamSkillAdd: { pending: skillPending }, tbTeamAddSkill
  } = useContext(SupervisorContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>();
  // Add Job dialog
  const [isOpen, setIsOpen] = useState(false);

  const handleAddOpen = useCallback(() => setIsOpen(true), []);
  const handleAddClose = useCallback(() => setIsOpen(false), []);

  const handleAddJob = useCallback((job?: JobLookupItem | null) => {
    setIsOpen(false);
    if (job?.id) tbAddTeam?.({
      job_id: job.id,
      onSuccess: (data?: TbAddTeamFeedback) => {
        const { team_id } = data || {};
        if (team_id) navigate(injectParams(PATH_HR_TEAM_BUILDER_TEAM, { team_id }));
      }
    });
  }, [navigate, tbAddTeam]);

  const handleAddSkill = useCallback((skill: Skill, level: SkillLevel) => {
    setIsOpen(false);
    if (skill?.id && level >= SKILL_LEVEL_FIRST) tbAddTeam?.({
      onSuccess: (data?: TbAddTeamFeedback) => {
        const { team_id } = data || {};
        if (team_id) {
          navigate(injectParams(PATH_HR_TEAM_BUILDER_TEAM, { team_id }));
          tbTeamAddSkill?.({
            team_id,
            skill_id: skill.id,
            skill_proficiency_level: level,
            skill
            // This action's failure is reported by `TeamBuilderTeam` on the next screen.
          });
        }
      }
    });
  }, [navigate, tbAddTeam, tbTeamAddSkill]);

  const addError = useMemo(
    () => getTbErrorMessage('hr.teambuilder.new_team_error', addParams?.errorCode),
    [addParams?.errorCode]
  );

  useEffect(() => {
    if (!isNil(pageSize)) requireTbTeams?.({
      limit: pageSize,
      offset: pageSize * (currentPage - 1)
    });
  }, [currentPage, pageSize, requireTbTeams]);

  return (
    <>
      <CardSection>
        <BoxTypography variant="subtitle1">
          <FormattedMessage id="hr.teambuilder.teams"/>
        </BoxTypography>
      </CardSection>
      <CardsGrid
          ref={ref}
          items={teams as (TbTeamCardItem[] | null | undefined)}
          variant="shady"
          pending={pending || !teams}
          failed={failed}
          disabled={addPending || skillPending}
          blendNotFound
          blendPagination
          component={TbTeamCard}
          ComponentProps={componentProps}
          notFoundMessage="hr.teambuilder.no_teams"
          pagination={(
            <PaginationControls
                settingsId="tb_teams"
                loaded={Boolean(teams)}
                pending={pending}
                loading={pending}
                total={count}
                currentPage={currentPage}
                onPageSelected={setCurrentPage}
                onPageSize={setPageSize}
                disabled={pending || failed}
                totalMessage="hr.teambuilder.pagination"
            />
          )}
      />
      <CardFooter className={footer}>
        <LargeButton
            title="hr.teambuilder.new_team"
            variant="cohort"
            onClick={handleAddOpen}
            disabled={failed || addPending || skillPending}
        />
      </CardFooter>
      <AddJobDialog
          isOpen={isOpen}
          onAdd={handleAddJob}
          onAddSkill={handleAddSkill}
          onCancel={handleAddClose}
          disabled={addPending || skillPending}
      />
      <ActionSnackbar
          severity="error"
          message={addError}
          open={addFailed}
      />
    </>
  );
});

TeamBuilder.displayName = 'TeamBuilder';

export default memo(TeamBuilder);
